import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface ErrorReport {
  id: string;
  timestamp: string;
  level: 'error' | 'warning' | 'info';
  message: string;
  stack?: string;
  url: string;
  userAgent: string;
  userId?: string;
  sessionId: string;
  context?: {
    component?: string;
    action?: string;
    endpoint?: string;
    httpStatus?: number;
    formData?: any;
    [key: string]: any;
  };
  fingerprint: string; // Para deduplicação
  notifiedToSupport: boolean;
}

export interface ErrorTrackingConfig {
  enableTracking: boolean;
  enableConsoleLog: boolean;
  enableNotifications: boolean;
  enableAutoRetry: boolean;
  trackingEndpoint?: string;
  maxRetries: number;
  batchSize: number;
  flushInterval: number; // ms
  maxQueueSize: number;
  enableDeduplication: boolean;
  deduplicationWindow: number; // ms
}

@Injectable({
  providedIn: 'root',
})
export class ErrorTrackingService {
  private http = inject(HttpClient);
  private config: ErrorTrackingConfig = {
    enableTracking: true,
    enableConsoleLog: !environment.production,
    enableNotifications: true,
    enableAutoRetry: true,
    trackingEndpoint: `${environment.apiUrl}/errors/v1/report`,
    maxRetries: 3,
    batchSize: 10,
    flushInterval: 30000, // 30s
    maxQueueSize: 100,
    enableDeduplication: true,
    deduplicationWindow: 300000, // 5min
  };

  private errorQueue: ErrorReport[] = [];
  private recentErrors: Map<string, number> = new Map(); // Para deduplicação
  private sessionId: string;
  private userId?: string;
  private flushInterval?: number;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.setupGlobalErrorHandlers();
    this.startBatchFlush();
  }

  /**
   * Gera um ID único para a sessão
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Gera fingerprint para deduplicação de erros
   */
  private generateFingerprint(error: Partial<ErrorReport>): string {
    const key = `${error.message}_${error.url}_${error.context?.component}`;
    return btoa(key).replace(/[+/=]/g, '').substr(0, 16);
  }

  /**
   * Configura handlers globais de erro
   */
  private setupGlobalErrorHandlers(): void {
    // Handler para erros JavaScript não capturados
    window.addEventListener('error', (event) => {
      this.reportError({
        level: 'error',
        message: event.message,
        stack: event.error?.stack,
        context: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    });

    // Handler para promises rejeitadas não capturadas
    window.addEventListener('unhandledrejection', (event) => {
      this.reportError({
        level: 'error',
        message: `Unhandled Promise Rejection: ${event.reason}`,
        stack: event.reason?.stack,
        context: {
          type: 'unhandledrejection',
          reason: event.reason,
        },
      });
    });
  }

  /**
   * Inicia o flush em lote dos erros
   */
  private startBatchFlush(): void {
    this.flushInterval = window.setInterval(() => {
      this.flushErrorQueue();
      this.cleanupOldErrors();
    }, this.config.flushInterval);
  }

  /**
   * Limpa erros antigos da deduplicação
   */
  private cleanupOldErrors(): void {
    const now = Date.now();
    const cutoff = now - this.config.deduplicationWindow;

    for (const [fingerprint, timestamp] of this.recentErrors.entries()) {
      if (timestamp < cutoff) {
        this.recentErrors.delete(fingerprint);
      }
    }
  }

  /**
   * Verifica se erro é duplicado
   */
  private isDuplicateError(fingerprint: string): boolean {
    if (!this.config.enableDeduplication) return false;

    const lastSeen = this.recentErrors.get(fingerprint);
    const now = Date.now();

    if (lastSeen && now - lastSeen < this.config.deduplicationWindow) {
      return true;
    }

    this.recentErrors.set(fingerprint, now);
    return false;
  }

  /**
   * Gerencia tamanho da fila
   */
  private manageQueueSize(): void {
    if (this.errorQueue.length > this.config.maxQueueSize) {
      // Remove erros mais antigos
      const excess = this.errorQueue.length - this.config.maxQueueSize;
      this.errorQueue.splice(0, excess);

      if (this.config.enableConsoleLog) {
        console.warn(
          `Error queue exceeded max size. Removed ${excess} old errors.`
        );
      }
    }
  }

  /**
   * Define o usuário atual
   */
  setUserId(userId: string): void {
    this.userId = userId;
  }

  /**
   * Atualiza configuração
   */
  updateConfig(config: Partial<ErrorTrackingConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Reporta um erro
   */
  reportError(errorData: {
    level?: 'error' | 'warning' | 'info';
    message: string;
    stack?: string;
    context?: any;
  }): string {
    if (!this.config.enableTracking) return '';

    const errorReport: ErrorReport = {
      id: this.generateErrorId(),
      timestamp: new Date().toISOString(),
      level: errorData.level || 'error',
      message: errorData.message,
      stack: errorData.stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      userId: this.userId,
      sessionId: this.sessionId,
      context: errorData.context,
      fingerprint: '',
      notifiedToSupport: false,
    };

    // Gerar fingerprint e verificar duplicação
    errorReport.fingerprint = this.generateFingerprint(errorReport);

    if (this.isDuplicateError(errorReport.fingerprint)) {
      if (this.config.enableConsoleLog) {
        console.log(`🔄 Duplicate error ignored: ${errorReport.fingerprint}`);
      }
      return errorReport.id;
    }

    // Log no console em desenvolvimento
    if (this.config.enableConsoleLog) {
      console.group(`🚨 Error Tracked [${errorReport.level.toUpperCase()}]`);
      console.error('Message:', errorReport.message);
      console.log('ID:', errorReport.id);
      console.log('Context:', errorReport.context);
      console.log('Fingerprint:', errorReport.fingerprint);
      if (errorReport.stack) console.log('Stack:', errorReport.stack);
      console.groupEnd();
    }

    // Adiciona à fila para envio
    this.errorQueue.push(errorReport);
    this.manageQueueSize();

    // Flush imediato para erros críticos
    if (errorReport.level === 'error' && this.config.enableAutoRetry) {
      setTimeout(() => this.flushErrorQueue(), 1000);
    }

    return errorReport.id;
  }

  /**
   * Reporta erro HTTP específico
   */
  reportHttpError(
    endpoint: string,
    status: number,
    error: any,
    context?: any
  ): string {
    return this.reportError({
      level: status >= 500 ? 'error' : 'warning',
      message: `HTTP ${status}: ${error.message || error}`,
      context: {
        type: 'http_error',
        endpoint,
        httpStatus: status,
        ...context,
      },
    });
  }

  /**
   * Reporta erro de validação
   */
  reportValidationError(
    component: string,
    field: string,
    error: string,
    formData?: any
  ): string {
    return this.reportError({
      level: 'warning',
      message: `Validation Error in ${component}: ${field} - ${error}`,
      context: {
        type: 'validation_error',
        component,
        field,
        formData,
      },
    });
  }

  /**
   * Gera ID único para erro
   */
  private generateErrorId(): string {
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Envia erros em lote para o backend
   */
  private flushErrorQueue(): void {
    if (this.errorQueue.length === 0 || !this.config.enableTracking) {
      return;
    }

    const batch = this.errorQueue.splice(0, this.config.batchSize);

    this.sendErrorBatch(batch).subscribe({
      next: (response) => {
        // Marca erros como notificados
        batch.forEach((error) => {
          error.notifiedToSupport = true;
        });

        if (this.config.enableConsoleLog) {
          console.log(`✅ ${batch.length} errors sent to support`);
        }
      },
      error: (err) => {
        // Recoloca erros na fila em caso de falha
        this.errorQueue.unshift(...batch);

        if (this.config.enableConsoleLog) {
          console.warn('Failed to send errors to support:', err);
        }
      },
    });
  }

  /**
   * Envia lote de erros para o backend
   */
  private sendErrorBatch(errors: ErrorReport[]): Observable<any> {
    if (!this.config.trackingEndpoint) {
      return of({ success: true }); // Mock success se não há endpoint
    }

    return this.http
      .post(this.config.trackingEndpoint, {
        errors,
        metadata: {
          timestamp: new Date().toISOString(),
          version: '1.0.0', // Versão da aplicação
          environment: environment.production ? 'production' : 'development',
        },
      })
      .pipe(
        tap(() => {
          errors.forEach((error) => {
            error.notifiedToSupport = true;
          });
        }),
        catchError((error) => {
          console.error('Error tracking service failed:', error);
          return of({ success: false, error });
        })
      );
  }

  /**
   * Obtém estatísticas de erros da sessão atual
   */
  getSessionStats(): {
    totalErrors: number;
    errorsByLevel: Record<string, number>;
    notifiedCount: number;
  } {
    const stats = {
      totalErrors: this.errorQueue.length,
      errorsByLevel: {} as Record<string, number>,
      notifiedCount: 0,
    };

    this.errorQueue.forEach((error) => {
      stats.errorsByLevel[error.level] =
        (stats.errorsByLevel[error.level] || 0) + 1;
      if (error.notifiedToSupport) stats.notifiedCount++;
    });

    return stats;
  }

  /**
   * Limpa fila de erros (útil para testes)
   */
  clearErrorQueue(): void {
    this.errorQueue = [];
  }
}
